import AddBoxIcon from "@mui/icons-material/AddBox";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ScheduleIcon from "@mui/icons-material/Schedule";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import BrowsingDialogs from "../../../components/dialogs/BrowsingDialogs";
import Collapse from "@mui/material/Collapse";
import { alpha, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import ScrollableBox from "../../../components/common/ScrollableBox";
import { useAlert } from "../../../context/AlertContext";
import { useBrowsingContext } from "../../../context/BrowsingContext";
import { useCustomNavigate } from "../../../hooks/useCustomNavigate";
import { useChatNavigate } from "../../../hooks/useChatNavigate";
import useSubscription from "../../../hooks/useSubscription";
import { apiRequest } from "../../../utils/api";
import { ColumnBox } from "../../../utils/styledBox";
import ChatItemMenu from "./ChatItemMenu";

interface ErrorResponse {
  success: boolean;
  is_free: boolean;
  message: string;
  max_chats?: number;
  current_count?: number;
}

interface ChatLimitsResponse {
  success: boolean;
  is_free: boolean;
  max_chats: number;
  current_count: number;
  latest_chat_uuid: string | null;
}

type ChatType = {
  name: string;
  path: string;
  last_history: string;
  time: string;
};

const BrowsingListSidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useCustomNavigate();
  const { setAlert } = useAlert();
  const [chats, setChats] = useState<ChatType[]>([]);
  const { triggerFetch } = useBrowsingContext();
  // ChatNavigateフックを利用
  const { navigateToChat, createAndNavigateToNewChat, getChatLimits } = useChatNavigate();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDeleteAllDialog, setOpenDeleteAllDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const [upgradeDialog, setUpgradeDialog] = useState(false);
  const { isFree } = useSubscription(); // フックを使用してプラン判別
  // chatLimitsを削除し、setChatLimitsのみ保持
  const [, setChatLimits] = useState<ChatLimitsResponse | null>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // イベントの伝播を停止
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenDeleteAllDialog = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setOpenDeleteAllDialog(true);
    handleMenuClose();
  };

  const handleCloseDeleteAllDialog = () => {
    setOpenDeleteAllDialog(false);
  };

  const handleConfirmDeleteAll = async () => {
    try {
      // チャット履歴を全て削除
      await apiRequest("/api/v1/chat/all", "DELETE");
      setAlert("success", t("browsing.deleteAllChatsSuccess"));
      get_chats(); // チャットリストを再取得
      // ナビゲートする際にnew chat画面に移動
      navigateToChat();
    } catch (error) {
      setAlert("error", t("common.errorDeleteAllChats"));
      console.error("Error deleting all chats:", error);
    }
    handleCloseDeleteAllDialog();
  };

  const getRelativeTimeString = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return t("browsing.time.now");
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return t("browsing.time.minutes", { count: diffInMinutes });
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return t("browsing.time.hours", { count: diffInHours });
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return t("browsing.time.days", { count: diffInDays });
    }

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
      return t("browsing.time.weeks", { count: diffInWeeks });
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return t("browsing.time.months", { count: diffInMonths });
    }

    const diffInYears = Math.floor(diffInDays / 365);
    return t("browsing.time.years", { count: diffInYears });

    // デフォルトの戻り値を追加
    return "";
  };

  // チャット作成の実行部分（制限チェック後に呼ばれる）
  const handleCloseDialog = () => {
    setOpenDeleteDialog(false);
    setUpgradeDialog(false);
    setOpenDeleteAllDialog(false);
  };

  const executeNewChatCreation = async () => {
    try {
      // 新しいフックの機能を使用
      const success = await createAndNavigateToNewChat();
      if (success) {
        setOpenBrowsing(true);
        get_chats().then((newChatsFromServer) => {
          setAlert("success", t("browsing.createChat"));
          setChats(newChatsFromServer);
        });
      }
    } catch (error: unknown) {
      handleChatCreationError(error);
    }
  };

  const handleConfirmDelete = async () => {
    setOpenDeleteDialog(false);
    try {
      const success = await createAndNavigateToNewChat();
      if (success) {
        await get_chats();
        setAlert("success", t("browsing.createChat"));
      }
    } catch (error) {
      console.error("Error creating chat with force delete:", error);
      if ((error as AxiosError).response) {
        console.error(
          "API Error details:",
          (error as AxiosError).response?.data,
          "Status:",
          (error as AxiosError).response?.status
        );
      }
      setAlert("error", t("common.error"));
    }
  };

  const handleUpgrade = () => {
    setUpgradeDialog(false);
    navigate("/pricing");
  };

  const handleCreateNewChat = async () => {
    setUpgradeDialog(false);
    try {
      const success = await createAndNavigateToNewChat();
      if (success) {
        await get_chats();
        setAlert("success", t("browsing.createChat"));
      }
    } catch (error) {
      console.error("Error creating chat with force delete:", error);
      if ((error as AxiosError).response) {
        console.error(
          "API Error details:",
          (error as AxiosError).response?.data,
          "Status:",
          (error as AxiosError).response?.status
        );
      }
      setAlert("error", t("common.error"));
    }
  };

  // チャット作成エラーハンドリング
  const handleChatCreationError = (error: unknown) => {
    const axiosError = error as AxiosError;
    console.error("Error creating new chat:", error);

    if (axiosError.response && axiosError.response.data) {
      console.log("Error response data:", axiosError.response.data);

      // APIからのエラーレスポンスをオブジェクトとして型付きで扱う
      const errorData = axiosError.response.data as ErrorResponse;

      // チャット数制限エラーの処理
      if (errorData.message === "chat_limit_exceeded") {
        console.log("Chat limit exceeded error detected");

        // フリープランかどうかの情報はレスポンスにもあるが、useSubscriptionの値を使う
        const responseIsFree = !!errorData.is_free;
        console.log("Response indicates user is free plan:", responseIsFree);

        // useSubscriptionからのisFree値を使用
        if (isFree) {
          // フリープランユーザーの場合はアップグレードダイアログを表示
          setUpgradeDialog(true);
        } else {
          // 有料プランユーザーの場合は古いチャットを削除する確認ダイアログを表示
          setOpenDeleteDialog(true);
        }
        return;
      }
    }

    // その他のエラー
    setAlert("error", t("common.error"));
  };

  // ユーザーがチャット追加ボタンをクリックしたときの処理
  const newChat = async () => {
    try {
      // チャット制限情報を取得
      const limits = await getChatLimits();

      if (!limits) {
        // 制限情報が取得できなかった場合は従来の方法で判断
        if (isFree && chats.length >= 2) {
          setUpgradeDialog(true);
        } else if (chats.length >= 20) {
          setOpenDeleteDialog(true);
        } else {
          executeNewChatCreation();
        }
        return;
      }

      // 制限情報を使用して判断
      if (limits.current_count >= limits.max_chats) {
        if (limits.is_free) {
          setUpgradeDialog(true);
        } else {
          setOpenDeleteDialog(true);
        }
      } else {
        // 制限内なら新しいチャットを作成
        executeNewChatCreation();
      }
    } catch (error) {
      console.error("Error in newChat function:", error);
      // エラーは上位で処理されるべき
    }
  };

  const get_chats = async (): Promise<ChatType[]> => {
    // 戻り値の型を指定
    try {
      const res = await apiRequest("/api/v1/chat/", "GET");
      return res.data.data as ChatType[]; // APIからのデータをChatTypeの配列として返す
    } catch (error) {
      console.error("An unknown error occurred:", error);
      return []; // エラー時は空の配列を返す
    }
  };

  useEffect(() => {
    get_chats().then(setChats); // 取得したチャットでステートを更新
    getChatLimits().then(setChatLimits); // チャット制限情報を取得
  }, [triggerFetch]);

  const theme = useTheme();
  const isSmOrLarger = useMediaQuery(theme.breakpoints.up("sm"));

  const [openBrowsing, setOpenBrowsing] = React.useState(false);

  const stopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center" }} pt={1}>
        <ListItem
          onClick={() => !isSmOrLarger && setOpenBrowsing(!openBrowsing)}
          sx={{ cursor: !isSmOrLarger ? "pointer" : "default" }}
        >
          <ListItemIcon>
            <ScheduleIcon fontSize={"small"} color={"primary"} />
          </ListItemIcon>
          <ListItemText sx={{ mr: 2 }}>
            <Typography variant={"body2"} component={"span"}>
              {t("browsing.history")}
            </Typography>
          </ListItemText>
          <IconButton
            aria-label="more"
            aria-controls="long-menu"
            aria-haspopup="true"
            onClick={handleMenuClick}
            size="small"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          {!isSmOrLarger && (
            <IconButton onClick={() => setOpenBrowsing(!openBrowsing)} size="small" sx={{ ml: 0.5 }}>
              {openBrowsing ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </ListItem>
      </Box>
      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <MenuItem onClick={handleOpenDeleteAllDialog} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <DeleteSweepIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText color="error">{t("browsing.deleteAllChats")}</ListItemText>
        </MenuItem>
      </Menu>
      <Dialog
        open={openDeleteAllDialog}
        onClose={handleCloseDeleteAllDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t("browsing.deleteAllChatsConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t("browsing.deleteAllChatsConfirmMessage")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteAllDialog}>{t("common.cancel")}</Button>
          <Button onClick={handleConfirmDeleteAll} autoFocus color="error">
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <ScrollableBox sx={{ borderRadius: "0 0 4px 4px" }}>
        <List>
          {isSmOrLarger ? (
            <List component="div" sx={{ display: "flex", flexDirection: "column" }}>
              {chats.map((chat, index) => (
                <Fade in={true} key={chat.path} timeout={index * 300}>
                  <ListItemButton
                    key={chat.path}
                    onClick={() => navigate(chat.path)}
                    selected={location.pathname.split("/")[3] === chat.path.split("/")[2]}
                    sx={{
                      "&:hover": {
                        backgroundColor: (theme) => alpha(theme.palette.primary.light, 0.1),
                      },
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <ListItemIcon>
                      <ChatBubbleOutlineIcon fontSize={"small"} color={"secondary"} />
                    </ListItemIcon>
                    <ListItemText
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maskImage: "linear-gradient(to left, transparent 1%, black 30%)",
                        flex: 1,
                      }}
                    >
                      <ColumnBox>
                        <Typography
                          variant={"body2"}
                          component={"span"}
                          fontWeight={location.pathname.split("/")[3] === chat.path.split("/")[2] ? 600 : 400}
                        >
                          {chat.name}
                        </Typography>
                        <Typography
                          variant={"caption"}
                          component={"span"}
                          fontWeight={location.pathname.split("/")[3] === chat.path.split("/")[2] ? 600 : 400}
                        >
                          {chat.last_history ? chat.last_history : t("browsing.noHistory")}
                        </Typography>
                        <Typography
                          variant={"caption"}
                          component={"span"}
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {chat.time && getRelativeTimeString(chat.time)}
                        </Typography>
                      </ColumnBox>
                    </ListItemText>
                    <ChatItemMenu chatUuid={chat.path.split("/")[2]} stopPropagation={stopPropagation} />
                  </ListItemButton>
                </Fade>
              ))}

              <Box sx={{ flexGrow: 1 }} />
            </List>
          ) : (
            <Collapse in={openBrowsing} timeout="auto" unmountOnExit>
              <List component="div">
                {chats.map((chat, index) => (
                  <Fade in={true} key={chat.path} timeout={index * 100}>
                    <ListItemButton
                      onClick={() => navigate(chat.path)}
                      selected={location.pathname.split("/")[3] === chat.path.split("/")[2]}
                      sx={{
                        "&:hover": {
                          backgroundColor: (theme) => alpha(theme.palette.primary.light, 0.1),
                        },
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <ListItemIcon>
                        <ChatBubbleOutlineIcon fontSize={"small"} color={"secondary"} />
                      </ListItemIcon>
                      <ListItemText
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maskImage: "linear-gradient(to left, transparent 1%, black 30%)",
                          flex: 1,
                        }}
                      >
                        <ColumnBox>
                          <Typography
                            variant={"body2"}
                            component={"span"}
                            fontWeight={location.pathname.split("/")[3] === chat.path.split("/")[2] ? 600 : 400}
                          >
                            {chat.name}
                          </Typography>
                          <Typography
                            variant={"caption"}
                            component={"span"}
                            fontWeight={location.pathname.split("/")[3] === chat.path.split("/")[2] ? 600 : 400}
                          >
                            {chat.last_history ? chat.last_history : t("browsing.noHistory")}
                          </Typography>
                          <Typography
                            variant={"caption"}
                            component={"span"}
                            color="text.secondary"
                            sx={{ fontSize: "0.75rem" }}
                          >
                            {chat.time && getRelativeTimeString(chat.time)}
                          </Typography>
                        </ColumnBox>
                      </ListItemText>
                      <ChatItemMenu chatUuid={chat.path.split("/")[2]} stopPropagation={stopPropagation} />
                    </ListItemButton>
                  </Fade>
                ))}
              </List>
            </Collapse>
          )}
        </List>
      </ScrollableBox>
      <Tooltip title={""} arrow placement="top">
        <span>
          {/* Tooltipの子要素はspan等のDOM要素である必要がある */}
          <ListItemButton
            onClick={() => newChat()}
            sx={{
              "&:hover": {
                backgroundColor: (theme) => alpha(theme.palette.primary.light, 0.1),
              },
            }}
          >
            <ListItemIcon>
              <AddBoxIcon fontSize={"small"} color={"primary"} />
            </ListItemIcon>
            <ListItemText>
              <Typography variant={"body2"} component={"span"}>
                {t("browsing.addChat")}
              </Typography>
            </ListItemText>
          </ListItemButton>
        </span>
      </Tooltip>
      <BrowsingDialogs
        openDeleteDialog={openDeleteDialog}
        upgradeDialog={upgradeDialog}
        handleCloseDialog={handleCloseDialog}
        handleConfirmDelete={handleConfirmDelete}
        handleUpgrade={handleUpgrade}
        handleCreateNewChat={handleCreateNewChat}
      />
    </Box>
  );
};
export default BrowsingListSidebar;
